import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'

import { Dropdown, message, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleRight, faAngleLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { BiMenuAltRight, BiX } from 'react-icons/bi'
import { MdOutlineSubdirectoryArrowRight } from 'react-icons/md'
import { FaRegCircleUser } from 'react-icons/fa6'

import './styles.scss'
import uris from '../../../config/uris'
import { UserProfile } from '../header-green'
import CustomDropdown from '../dropdowns'
import PlanAlert from './plan-details'
import ESGSureLogo from '../../images/ESG-Sure-Logo.svg'
import ESGSureLogoMini from '../../images/ESG-Chat-logo-icon.svg'
import sidebarMenuData, { additionalMenus, userBasedMenus, userBasedRestrictedSubMenus, userHelperMenus } from './sidebar-data/sidebar-data'
import { decryptData } from '../../../helpers/encrypt/crypto-encrypt'
import { LogoutAction, LogoutReset } from '../../../store/slices/auth/logoutSlice'

const menuValidations = (data) => {
  let license_decrypt = decryptData(sessionStorage.getItem('license'))
  const sessionStoredTabDetails = license_decrypt?.accessLimits
  const storedPlaybookDetails = JSON.parse(sessionStoredTabDetails)

  const currentPlaybookTabs = storedPlaybookDetails?.playBooks
    ?.filter(playbook => playbook?.key && playbook?.value?.length > 0)
    .map(playbook => playbook.key) || [];

  if ((data?.key === "playbook") && (currentPlaybookTabs?.length > 0)) {
    const filteredSubMenu = data?.subMenu.filter(item => {

      return item?.isValidationRequired ? currentPlaybookTabs.includes(item.name) : true
    });

    return filteredSubMenu
  } else {
    return data?.subMenu
  }
}

const ExpandCollapseMenu = ({
  data,
  currentUrlPath,
  hideSidebar,
  closeSidebar,
  selectedIndexExpand,
  setSelectedIndexExpand,
  nestedSelectedIndexExpand,
  setNestedSelectedIndexExpand,
  validatedSubmenus
}) => {
  const handleClick = (id) => {
    setSelectedIndexExpand(id !== selectedIndexExpand ? id : 0)
  }

  const handleNestedClick = (id) => {
    setNestedSelectedIndexExpand(id !== nestedSelectedIndexExpand ? id : 0)
  }

  useEffect(() => {
    const isActive = (item) =>
      item.link === currentUrlPath ||
      item.refLink?.includes(currentUrlPath) ||
      item.subMenu?.some(isActive) ||
      item.children?.some(isActive);

    if (isActive(data)) {
      setSelectedIndexExpand(data.id);

      const activeSub = data.subMenu?.find(isActive);
      if (activeSub) setNestedSelectedIndexExpand(activeSub.id);
    }
  }, [currentUrlPath, data, setSelectedIndexExpand, setNestedSelectedIndexExpand]);


  return (
    <div className="menu-container" key={data?.id}>
      <div className="menu-submenu-container">
        <NavLink
          to={data.link || '#'}
          className={`menu ${data.active ? 'menu-active' : ''} ${data.refLink?.includes(currentUrlPath) || currentUrlPath === data?.link ? 'menu-active' : ''}`}
          onClick={() => handleClick(data?.id)}
        >
          <div className={`left-content`}>
            {data.icon}
            <div className="menu-text">{data.name}</div>
          </div>
          {data.showArrow && (
            <img
              className={`vector-2 vector-2-inverted ${selectedIndexExpand === data?.id ? 'menu-expanded' : 'menu-collapsed'}`}
              alt="Vector"
              src="https://cdn.animaapp.com/projects/64fedbf8904ea957862af339/releases/64fee3a4d977b32f06dd2856/img/vector-1.svg"
            />
          )}
        </NavLink>

        {selectedIndexExpand === data?.id &&
          data?.subMenu &&
          validatedSubmenus(data?.subMenu)?.map((subMenu) => {
            return (
              <div key={subMenu.id}>
                <NavLink
                  to={subMenu.link || '#'}
                  className={`sub-menu ${(subMenu.refLink?.includes(currentUrlPath) || subMenu.link === currentUrlPath) && 'menu-active'}
              ${hideSidebar && 'hidden-sidebar-contents'}
              `}
                  key={subMenu.id}
                  onClick={() => {
                    closeSidebar()
                    handleNestedClick(subMenu.id)
                  }}
                >
                  <div className={`${subMenu?.showArrow ? "left-content" : "normal-menu-content"}`}>
                    {(subMenu.refLink?.includes(currentUrlPath) || subMenu.link === currentUrlPath) && subMenu.active ? <FontAwesomeIcon icon={faArrowRight} className="arrow" /> : <div className="ellipse" />}
                    <div className={`menu-text ${subMenu.showArrow ? 'has-nested' : ""}`}>{subMenu.name}</div>
                  </div>
                  {subMenu.showArrow && (
                    <img
                      className={`vector-2 vector-2-inverted ${nestedSelectedIndexExpand === subMenu?.id ? 'menu-expanded' : 'menu-collapsed'}`}
                      alt="Vector"
                      src="https://cdn.animaapp.com/projects/64fedbf8904ea957862af339/releases/64fee3a4d977b32f06dd2856/img/vector-1.svg"
                    />
                  )}
                </NavLink>

                {nestedSelectedIndexExpand === subMenu.id &&
                  subMenu?.children?.map((nested) => (                    
                    <NavLink
                      to={nested.link || '#'}
                      className={`nested-sub-menu ${nested.link === currentUrlPath && nested.active && 'menu-active'}
              ${hideSidebar && 'hidden-sidebar-contents'}
              `}
                      key={nested.id}
                      onClick={closeSidebar}
                    >
                      {nested.link === currentUrlPath && nested.active ? <FontAwesomeIcon icon={faArrowRight} className="arrow" /> : <div className="ellipse">h</div>}
                      <div className="menu-text">{nested.name}</div>
                    </NavLink>
                  ))}
              </div>
            )
          })}
      </div>
    </div>
  )
}

const Sidebar = ({ setHideSidebar, hideSidebar }) => {
  const [sidebarData, setSidebarData] = useState([])
  const [showMobileSidebar, setShowMobileSidebar] = useState(false)
  const [buttonPosition, setButtonPosition] = useState('50%')
  const [selectedIndexExpand, setSelectedIndexExpand] = useState(0)
  const [nestedSelectedIndexExpand, setNestedSelectedIndexExpand] = useState(0)
  const [helperMenus, setHelperMenus] = useState([])
  const [restrictedSubMenus, setRestrictedSubMenus] = useState([''])
  const [planDetails, setPlanDetails] = useState({})
  const [menuSectionHeight, setMenuSectionHeight] = useState("100%")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const sidebarRef = useRef()
  const userProfileRef = useRef(null);
  const currentUrlPath = window.location.pathname
  const { otp: userDetails } = useSelector((state) => state.otp)
  const { logout, error: logoutError } = useSelector((state) => state.logout)

  const handleLogout = () => {
    dispatch(LogoutAction())
  }

  const structureDropMenu = [
    {
      key: 1,
      label: <UserProfile handleLogout={handleLogout} />,
    },
  ]

  const handleShowSidebar = () => {
    setShowMobileSidebar(!showMobileSidebar)
  }

  const handleMouseMove = (event) => {
    if (sidebarRef.current) {
      const containerRect = sidebarRef.current.getBoundingClientRect()
      const currentPointer = event.clientY - containerRect.top

      setButtonPosition(currentPointer - 20)
    }
  }

  const closeSidebar = () => {
    if (showMobileSidebar) {
      setShowMobileSidebar(false)
    }
  }

  const handleUserButtonActions = (action) => {
    if (action === 'intercom-support') {
      window.Intercom('show')
    }
  }

  const validatedSubmenus = (submenus) => {
    if (restrictedSubMenus?.length > 0) {
      const filteredMenus = submenus?.filter((subMenu) => !restrictedSubMenus?.includes(subMenu?.key))

      return filteredMenus;
    } else {
      return submenus;
    }
  };

  useEffect(() => {
    let license_decrypt = decryptData(sessionStorage?.getItem('license'))
    const details = license_decrypt?.planDetails

    const planContents = details ? JSON.parse(details) : {}
    if (planContents) {
      setPlanDetails(planContents)
    }
  }, [])

  useEffect(() => {
    let user_decrypt = decryptData(sessionStorage?.getItem('rqewseu'))
    let role_decrypt = decryptData(sessionStorage.getItem('eqlwoer'))
    const currentUser = userDetails?.user?.email || user_decrypt?.usermail
    const mailSuffix = currentUser?.split('@')[1]

    const isAdmin = (currentUser === "arunkumar.doraisamy@esgds.io") || (role_decrypt?.role === "admin")
    const isTrailUser = planDetails?.planName === "Trial"
    const isPromptAdmin = role_decrypt?.role === "prompt_admin"

    let userType = "external-domain";

    if (isTrailUser) {
      userType = "trial-user";
    } else if (isAdmin) {
      userType = "esgds-admin";
    } else if (isPromptAdmin) {
      userType = "prompt_admin";
    } else if (mailSuffix && userBasedMenus[mailSuffix]) {
      userType = mailSuffix;
    }

    // Main Menus
    const selectedMenus = userBasedMenus[userType]
    const assignedMenus = sidebarMenuData?.filter((menu) => selectedMenus?.includes(menu.key));

    const restrictableSubMenu = userBasedRestrictedSubMenus[userType] || [""];
    const validatedMenus = assignedMenus?.map((menuItem) => ({ ...menuItem, subMenu: menuValidations(menuItem) }))

    // User Helper Menus
    const newUserHelperMenus = userHelperMenus.filter(({ restrictedUsers = [], unRestrictedUsers = [] }) => {
      if (!restrictedUsers.length && !unRestrictedUsers.length) return true;
      if ([userType, currentUser, mailSuffix].some(user => restrictedUsers.includes(user))) return false;
      return !unRestrictedUsers.length || unRestrictedUsers.includes(currentUser) || unRestrictedUsers.includes(mailSuffix);
    });

    setHelperMenus(newUserHelperMenus)
    setSidebarData(validatedMenus);
    setRestrictedSubMenus(restrictableSubMenu)
  }, [planDetails])

  useEffect(() => {
    if (logout?.message) {
      dispatch(LogoutReset())
      dispatch({ type: 'RESET' })
      navigate(uris.login)
    }
  }, [logout?.message])

  useEffect(() => {
    if (logoutError?.message) {
      message?.error(logoutError?.message)
    }
  }, [logoutError?.message])

  useEffect(() => {
    if (planDetails?.planName === "Trial") {
      setHelperMenus((prevMenus) => [additionalMenus?.bookDemo, ...prevMenus]);
    }
  }, [planDetails?.planName])

  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        const userHeight = userProfileRef.current?.offsetHeight || 0;
        const totalHeight = window.innerHeight;
        const paddingHeight = 60;
        setMenuSectionHeight(`${totalHeight - userHeight - paddingHeight}px`);
      }, 100);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);


  return (
    <div className={`sidebar-index ${showMobileSidebar ? 'mobile-menu-full' : 'mobile-menu-hidden'}`} ref={sidebarRef} onMouseMove={handleMouseMove}>
      {/* Expand/Collapse Button */}
      <div className="expand-collapse-button" onClick={() => setHideSidebar(hideSidebar ? false : true)}>
        <div className="icon-wrapper" style={{ top: buttonPosition }}>
          {hideSidebar ? <FontAwesomeIcon icon={faAngleRight} /> : <FontAwesomeIcon icon={faAngleLeft} />}
        </div>
      </div>

      {/* Logo */}
      <div className="logo-container menu-hidden">
        {hideSidebar ? <img className="logo-image mini" alt="ESG Sure Logo" src={ESGSureLogoMini} /> : <img className="logo-image" alt="ESG Sure Logo" src={ESGSureLogo} />}
        <div className="menu-icon" onClick={() => handleShowSidebar()}>
          {!showMobileSidebar ? <BiMenuAltRight /> : <BiX />}
        </div>
      </div>

      <div className="menu-section" style={{ height: menuSectionHeight }}>
        {/* Full menu */}
        {!hideSidebar &&
          sidebarData?.length > 0 &&
          sidebarData?.map((data) => (
            <ExpandCollapseMenu
              data={data}
              currentUrlPath={currentUrlPath}
              hideSidebar={hideSidebar}
              key={data.id}
              closeSidebar={closeSidebar}
              setSelectedIndexExpand={setSelectedIndexExpand}
              selectedIndexExpand={selectedIndexExpand}
              nestedSelectedIndexExpand={nestedSelectedIndexExpand}
              setNestedSelectedIndexExpand={setNestedSelectedIndexExpand}
              validatedSubmenus={validatedSubmenus}
            />
          ))}

        {/* Collapse Sidebar */}
        {hideSidebar &&
          sidebarData?.length > 0 &&
          sidebarData?.map((data) => {
            const dropTitle = [
              {
                key: data.id,
                label: <NavLink className="dropdown-title">{data.name}</NavLink>,
              },
            ];

            // Flatten submenus and their children
            const formattedData = dropTitle.concat(
              data?.subMenu &&
              validatedSubmenus(data.subMenu)?.flatMap((item) => {
                const isChildActive = item.children?.some(
                  (child) => child.link === currentUrlPath && child.active
                );
                const submenuItem = {
                  key: item.name,
                  label: (
                    <NavLink
                      to={item.link}
                      className={`sub-menu ${(item.link === currentUrlPath && item.active) || isChildActive
                          ? 'menu-active'
                          : ''
                        } ${hideSidebar && 'hidden-sidebar-contents'}`}
                      onClick={() => {
                        closeSidebar();
                      }}
                    >
                      <div className="ellipse">.</div>
                      <div className="menu-text">{item.name}</div>
                    </NavLink>
                  ),
                };

                const childItems =
                  item.children?.map((child) => ({
                    key: child.name,
                    label: (
                      <NavLink
                        to={child.link}
                        className={`nested-sub-menu children ${child.link === currentUrlPath && child.active && 'menu-active'} ${hideSidebar && 'hidden-sidebar-contents'
                          }`}
                        onClick={() => {
                          closeSidebar();
                        }}
                      >
                        <div className="ellipse">.</div>
                        <div className="menu-text">{child.name}</div>
                      </NavLink>
                    ),
                  })) || [];

                return [submenuItem, ...childItems];
              })
            );

            return (
              <div className="menu-container" key={data?.id}>
                {data.subMenu ? (
                  <CustomDropdown content={{ items: formattedData }} placement="rightTop">
                    {data?.type === 'menu' && (
                      <NavLink
                        to={data.link}
                        className={`menu ${data.active ? 'menu-active' : ''}`}
                      >
                        <div className={`left-content`}>{data.icon}</div>
                      </NavLink>
                    )}
                  </CustomDropdown>
                ) : (
                  <>
                    {data?.type === 'menu' && (
                      <Tooltip
                        title={<div style={{ color: '#7b6631' }}>{data.name}</div>}
                        color="#F2F0EA"
                        className="tooltip-text"
                        placement="right"
                      >
                        <NavLink
                          to={data.link}
                          className={`menu ${data.link === currentUrlPath ? 'menu-active' : ''} hidden-menu`}
                        >
                          <div className={`left-content`}>{data.icon}</div>
                        </NavLink>
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
            );
          })}

      </div>

      <div className="user-profile" ref={userProfileRef}>
        {/* Plan Alert */}
        {planDetails?.endDate && <PlanAlert hideSideBar={hideSidebar} planDetails={planDetails} />}

        {helperMenus?.map((menu) => {
          return (
            <NavLink
              key={menu?.id}
              target={menu?.isExternal ? '_blank' : '_self'}
              to={menu?.isActive ? menu?.link : currentUrlPath}
              className="user-profile-menu-link"
              onClick={() => closeSidebar()}
            >
              <div
                className={`section-card ${menu?.link === currentUrlPath ? 'menu-active' : ''} ${menu?.isActive ? 'hoverable-menu' : ''}`}
                onClick={() => menu?.action && handleUserButtonActions(menu?.action)}
              >
                {!hideSidebar ? (
                  <>
                    <div className="icon-wrapper">{menu.icon}</div>
                    <div className="text-wrapper">{menu?.name}</div>
                  </>
                ) : (
                  <Tooltip title={<div style={{ color: '#7b6631', cursor: 'pointer' }}>{menu?.name}</div>} color="#FFFFFF" className="tooltip-text" placement="right">
                    <div className="icon-wrapper">{menu?.icon}</div>
                  </Tooltip>
                )}
              </div>
            </NavLink>
          )
        })}

        <Dropdown
          menu={{ items: structureDropMenu }}
          placement="topLeft"
          arrow={{
            pointAtCenter: true,
          }}
        >
          <div className="section-card">
            <div className="icon-wrapper">
              <FaRegCircleUser />
            </div>
            {!hideSidebar && <div className="text-wrapper">User Profile</div>}
          </div>
        </Dropdown>
      </div>
    </div>
  )
}

export default Sidebar
